import { EventListeners, MinzeElement, Reactive } from 'minze'
import { Locale } from '../../modules/locale'
import { EMAIL_REGEX, escapeHtml, unescapeHtml } from '../../modules/utils'
import MainModule from '../../modules/main'
import { Callback } from '../../modules/callback'
import { TurnstileChallenge } from '../../utils/cloudflare/TurnstileChallenge'
import { config } from '../../constants/config'

export interface LvcContactForm {
  errors: { [key: string]: string | undefined }
  values: { [key: string]: string | undefined }
}

const submitForm = async (endpoint: string, body: object) => {
  const rawResponse = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  return await rawResponse.json()
}

Locale.getInstance().registerKeySet('fr', 'contactForm', {
  firstNameLabel: 'Prénom',
  firstNamePlaceholder: 'Écrivez ici...',
  lastNameLabel: 'Nom',
  lastNamePlaceholder: 'Nom',
  emailLabel: 'Adresse courriel',
  emailPlaceholder: 'courriel@gmail.com',
  subjectLabel: 'Sujet',
  subjectPlaceholder: 'Choisir',
  subjectSelectLabels: `Général,Création et gestion du compte,Support technique,Se joindre à l'équipe,Général,Création et gestion du compte pro,Afficher ma programmation ou mes événements sur La Vitrine,Faire une demande de modification,Support technique,Service de contenus commandités`,
  subjectSelectValues: `b2c-general,b2c-account,b2c-support,b2c-join-team,b2b-general,b2b-account,b2b-display,b2b-modification,b2b-support,b2b-sponsored-content`,
  subjectOptgroupLabels: 'Utilisateur du site,Professionnel de la culture',
  messageLabel: 'Message',
  messagePlaceholder: 'Message',
  submit: 'Soumettre',
  errors: {
    invalidEmail: 'Adresse courriel invalide',
    required: 'Ce champ est requis',
    submissionFailed: `Une erreur s'est produite lors de la soumission du formulaire. Veuillez réessayer.`,
    validationFailed: 'Veuillez corriger les erreurs dans le formulaire.',
    turnstileFailed: 'La vérification de sécurité a échoué. Veuillez réessayer.',
  },
  successMessage: 'Merci! Le formulaire à été soumis avec succès!',
  successMessageWithTicket: 'Merci! Votre demande a été soumise avec succès. Numéro de ticket: ',
})

Locale.getInstance().registerKeySet('en', 'contactForm', {
  firstNameLabel: 'First name',
  firstNamePlaceholder: 'Write here…',
  lastNameLabel: 'Last name',
  lastNamePlaceholder: 'Last name',
  emailLabel: 'E-mail',
  emailPlaceholder: 'email@gmail.com',
  subjectLabel: 'Subject',
  subjectPlaceholder: 'Choose',
  subjectSelectLabels:
    'General,Creating and managing my account,Technical support,Join our team,General,Creating and managing my professional account,Display my programming or events on La Vitrine,Submit a change request for an event,Technical support,Sponsored Content Service',
  subjectSelectValues:
    'b2c-general,b2c-account,b2c-support,b2c-join-team,b2b-general,b2b-account,b2b-display,b2b-modification,b2b-support,b2b-sponsored-content',
  subjectOptgroupLabels: 'Site user,Culture professional',
  messageLabel: 'Message',
  messagePlaceholder: 'Message',
  submit: 'Submit',
  errors: {
    invalidEmail: 'Invalid email',
    required: 'This field is required',
    submissionFailed: 'An error occurred while submitting the form. Please try again.',
    validationFailed: 'Please correct the errors in the form.',
    turnstileFailed: 'Security verification failed. Please try again.',
  },
  successMessage: 'Thank you! The form has been submitted successfully!',
  successMessageWithTicket:
    'Thank you! Your request has been submitted successfully. Ticket number: ',
})

export class LvcContactForm extends MinzeElement {
  attrs = []
  reactive: Reactive = [
    ['values', {}],
    ['errors', {}],
    ['isLoading', false],
  ]

  private onChangeCb: string = ''
  private turnstile: TurnstileChallenge | null = null
  private cfTurnstileToken: string | null = null

  renderInput = (name: string, textarea: boolean = false) => {
    return `
      <lvc-input
        id="${name}"
        name="${name}"
        placeholder="${Locale.getKey(`contactForm.${name}Placeholder`)}"
        default-value="${escapeHtml(this.values[name] || '')}"
        label="${Locale.getKey(`contactForm.${name}Label`)}"
        error="${this.errors[name]}"
        textarea="${textarea}"
        on-change="${this.onChangeCb}"
      ></lvc-input>
    `
  }

  onChange() {
    // Get values of all the inputs
    const inputIds = ['firstName', 'lastName', 'email', 'subject', 'message']
    const values = inputIds.reduce(
      (acc, id) => {
        const element = this.select(`#${id}`) as HTMLInputElement
        acc[id] = element?.value || ''
        return acc
      },
      {} as { [key: string]: string }
    )

    // Check if there are any non-empty values
    const hasValues = Object.values(values).some(value => value.trim() !== '')

    // Set the page to dirty or clearDirty accordingly
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()
    if (hasValues) {
      routing.setDirty()
    } else {
      routing.clearDirty()
    }
  }

  async onReady() {
    this.onChangeCb = Callback.attach(this.onChange.bind(this))
    // Initialize invisible Turnstile
    this.turnstile = new TurnstileChallenge()
    this.rerender()
  }

  async prepareTurnstileToken(): Promise<string> {
    if (!this.turnstile) {
      throw new Error('Turnstile is not initialized')
    }
    try {
      // Always obtain a fresh token per attempt
      const token = await this.turnstile.initialize()
      this.cfTurnstileToken = token
      return token
    } catch (error) {
      console.error('Error during Turnstile initialization:', error)
      throw error
    }
  }

  submit = async () => {
    let values: { [key: string]: string } = {}
    Object.keys(this.errors).forEach(key => (this.errors[key] = undefined))
    const getValue = (id: string) => {
      const element = this.select(`#${id}`) as HTMLInputElement
      values[id] = unescapeHtml(element?.value)
    }
    ;['firstName', 'lastName', 'email', 'subject', 'message'].forEach(id => getValue(id))
    if (!values['firstName'])
      this.errors['firstName'] = Locale.getKey('contactForm.errors.required')
    if (!values['lastName']) this.errors['lastName'] = Locale.getKey('contactForm.errors.required')
    if (!EMAIL_REGEX.test(values['email'])) {
      this.errors.email = Locale.getKey('contactForm.errors.invalidEmail')
    }
    if (!values['email']) this.errors['email'] = Locale.getKey('contactForm.errors.required')
    if (!values['subject']) this.errors['subject'] = Locale.getKey('contactForm.errors.required')
    if (!values['message']) this.errors['message'] = Locale.getKey('contactForm.errors.required')

    Object.keys(values).forEach(key => (this.values[key] = values[key]))

    if (!Object.values(this.errors)?.filter(error => error).length) {
      this.isLoading = true
      try {
        // Always get a fresh Turnstile token for each submission
        await this.prepareTurnstileToken()

        // Submit to backend API
        const endpoint = `${config.backendApi}/user/public/submit_support_form`
        const response = await submitForm(endpoint, {
          firstName: this.values['firstName'],
          lastName: this.values['lastName'],
          email: this.values['email'],
          subjectCode: this.values['subject'],
          message: this.values['message'],
          // Backend expects a Turnstile token; send freshly obtained token
          turnstileToken: this.cfTurnstileToken,
          lang: Locale.language,
        })

        // Handle successful submission
        if (response.status === 'success') {
          this.submitted = true
          this.values = {}
          const mainModule = MainModule.getInstance()
          mainModule.getRouting().clearDirty()

          // Show success message with ticket ID if available
          let successMessage = Locale.getKey('contactForm.successMessage')
          if (response.ticketId) {
            successMessage =
              Locale.getKey('contactForm.successMessageWithTicket') + response.ticketId
          }

          mainModule.showToastAlert(successMessage)
          this.rerender(true)
        }
        // Handle error response from backend
        else if (response.status === 'error') {
          console.error('Backend error:', response.message)
          const mainModule = MainModule.getInstance()

          // Check if it's a validation error
          if (response.message?.includes('Validation failed')) {
            mainModule.showToastAlert(Locale.getKey('contactForm.errors.validationFailed'))
          }
          // Check if it's a Turnstile verification error
          else if (
            response.message?.includes('Bot protection') ||
            response.message?.includes('Turnstile')
          ) {
            mainModule.showToastAlert(Locale.getKey('contactForm.errors.turnstileFailed'))
            // Reset Turnstile token so it gets regenerated on next submission
            this.cfTurnstileToken = null
          }
          // Generic error
          else {
            mainModule.showToastAlert(
              response.message || Locale.getKey('contactForm.errors.submissionFailed')
            )
          }
        }
      } catch (error) {
        console.error('Form submission error:', error)
        const mainModule = MainModule.getInstance()
        mainModule.showToastAlert(Locale.getKey('contactForm.errors.submissionFailed'))
      } finally {
        // Clear token after each attempt to avoid reusing single-use tokens
        this.cfTurnstileToken = null
        this.isLoading = false
      }
    } else {
      this.rerender(true)
    }
  }

  html = () => `
    <lvc-box display="flex" flexDirection="column" rowGap="16px">
      <lvc-box display="flex" columnGap="16px">
        ${this.renderInput('firstName')}
        ${this.renderInput('lastName')}
      </lvc-box>
      <lvc-box>
        ${this.renderInput('email')}
      </lvc-box>
      <lvc-box>
        <lvc-select
          id="subject"
          name="subject"
          placeholder="${Locale.getKey('contactForm.subjectPlaceholder')}"
          default-value="${this.values.subject || ''}"
          label="${Locale.getKey('contactForm.subjectLabel')}"
          selectOptionLabels="${Locale.getKey('contactForm.subjectSelectLabels')}"
          selectOptions="${Locale.getKey('contactForm.subjectSelectValues')}"
          selectOptgroupLabels="${Locale.getKey('contactForm.subjectOptgroupLabels')}"
          error="${this.errors.subject}"
        ></lvc-select>
      </lvc-box>
      <lvc-box>
        ${this.renderInput('message', true)}
      </lvc-box>
    </lvc-box>
    <lvc-box display="flex" justifyContent="center" mt="16px">
      <lvc-button loading="${this.isLoading}" enable-hover-box-shadow="true" font-size="1rem" padding="0.5rem 52px" font-weight="600" width="auto" id="submit">
        ${Locale.getKey('contactForm.submit')}
      </lvc-button>
    </lvc-box>
  `

  css = () => ``

  eventListeners: EventListeners = [['#submit', 'click', this.submit.bind(this)]]
}
